/**
 * OutputFileNameField — the "Output file" section's input, helper text and
 * advisory duplicate warning (sub-task 03 R1.2/R1.5/R1.6, packet 3.1).
 *
 * Purely presentational. The suggestion, the probe and the duplicate decision
 * are the caller's (`AiReviewPage` + `useOutputFilenameSuggestion`), so this
 * component has no idea a version convention exists — it renders a name the
 * recruiter fully owns.
 *
 * D7 is explicit that the duplicate warning is ADVISORY: it never disables the
 * input, never blocks the run, and nothing here ever rewrites `value` (no
 * masking, no forced `.csv` on blur). The only blocking validation in R1 is
 * the empty case, and that lives on the page, next to the other Apply gates.
 */

export interface OutputFileNameFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Render the advisory duplicate warning. The caller suppresses this when
   *  the probe was truncated (R1.9) rather than claiming a false negative. */
  duplicate: boolean;
  /** Freeze the name once the run that will produce the file owns it (R1.7,
   *  widened in packet 3.3 to `succeeded` onward). */
  readOnly?: boolean;
}

/** Why the field went read-only. Shown WITH the frozen name, never instead of
 *  it: a control that stops accepting input without saying why reads as a bug.
 *  Only sensible because the name is genuinely un-changeable for this run —
 *  the apply call carries no body, so the create-time name is the one used. */
const LOCKED_HINT = "This title belongs to the current run. Start a new run to use a different one.";

export function OutputFileNameField({
  value,
  onChange,
  duplicate,
  readOnly = false,
}: OutputFileNameFieldProps) {
  return (
    <div className="flex max-w-xl flex-col gap-2">
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        readOnly={readOnly}
        aria-label="People list title"
        data-testid="output-filename-input"
        className="w-full rounded-sm border border-border bg-bg px-2.5 py-1.5 text-body text-text placeholder:text-text-muted hover:border-border-hover focus:border-border-hover focus:outline-none read-only:text-text-secondary"
      />

      <p className="text-caption text-text-muted" data-testid="output-filename-helper">
        The review is saved as a new people list. The original people list is not changed.
      </p>

      {readOnly ? (
        <p className="text-caption text-text-muted" data-testid="output-filename-locked-hint">
          {LOCKED_HINT}
        </p>
      ) : null}

      {/* `<output>` rather than a `role="status"` paragraph: it carries the
          same polite live-region semantics (so the warning is announced when
          it appears mid-typing) and is the semantic element biome's
          `useSemanticElements` rule requires. */}
      {duplicate ? (
        <output
          data-testid="output-filename-duplicate-warning"
          className="block rounded-md border border-warning-strong bg-warning-muted px-3 py-2 text-caption text-warning-ink"
        >
          {/* The name is QUOTED (house style, packet 3.2 decision 2): it is
              free text, so a name with a space in it must not dissolve into
              the surrounding sentence. Matches the confirm dialog's C2 copy. */}
          A people list named "{value}" already exists for the list you are reviewing. Saving creates a second one
          with the same title.
        </output>
      ) : null}
    </div>
  );
}
